'use client';
/**
 * OfflineProvider
 * 
 * Initializes the offline database and background sync on app mount.
 * Wraps children and provides the OfflineIndicator UI.
 */


import { useEffect, useRef } from 'react';
import { getOfflineDB } from '@/lib/offline/db';
import { startBackgroundSync, stopBackgroundSync } from '@/lib/offline/sync';
import { registerServiceWorker } from '@/lib/offline/register-sw';
import { OfflineIndicator } from '@/components/molecules/OfflineIndicator';

interface OfflineProviderProps {
  children: React.ReactNode;
}

export function OfflineProvider({ children }: OfflineProviderProps) {
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    async function init() {
      try {
        // 1. Initialize SQLite WASM database
        await getOfflineDB();
        console.log('[Offline] Database initialized');

        // 2. Register Service Worker
        registerServiceWorker();

        // 3. Prefetch all main pages for offline use
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.ready.then((registration) => {
            registration.active?.postMessage({
              type: 'PREFETCH_PAGES',
              urls: [
                '/reception',
                '/examination', 
                '/lab',
                '/prescriptions',
                '/pos',
                '/inventory',
                '/services',
                '/orders',
                '/invoices',
                '/compensation',
                '/settings',
                '/users',
              ],
            });
          });
        }

        // 4. Start background sync
        startBackgroundSync();

        // 5. Auto-purge stale cache data (24h without sync)
        const { autoPurgeStaleData } = await import('@/lib/offline/clear-offline-data');
        await autoPurgeStaleData();
      } catch (error) {
        console.error('[Offline] Initialization failed:', error);
        // App continues to work online-only — no crash
      }
    }

    init();

    return () => {
      stopBackgroundSync();
    };
  }, []);

  return (
    <>
      {children}
      <OfflineIndicator />
    </>
  );
}
